import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../utils/apiClient';
import config from '../utils/config';

export interface LabUser {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  first_name: string;
  last_name: string;
  password?: string;
  address: string;
  role: string;
  lab_name: string;
  lab_branch_email: string;
  lab_mobile_number: string;
}

export interface SuperAdmin {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  mobile_number: string;
  role: string;
}

interface UsersState {
  labUsers: LabUser[];
  superAdmins: SuperAdmin[];
  loading: boolean;
  error: string | null;
}

const initialState: UsersState = {
  labUsers: [],
  superAdmins: [],
  loading: false,
  error: null,
};

export const fetchLabUsers = createAsyncThunk(
  'users/fetchLabUsers',
  async (_, thunkAPI) => {
    try {
      const response = await apiClient.get(`${config.services.ums}/api/v1/lab-users`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch lab users');
    }
  }
);

export const fetchSuperAdmins = createAsyncThunk(
  'users/fetchSuperAdmins',
  async (_, thunkAPI) => {
    try {
      const response = await apiClient.get(`${config.services.ums}/api/v1/super-admins`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch super admins');
    }
  }
);

export const createLabUser = createAsyncThunk(
  'users/createLabUser',
  async (data: Partial<LabUser> & { password?: string }, thunkAPI) => {
    try {
      const response = await apiClient.post(`${config.services.ums}/api/v1/lab-users`, {
        first_name: data.first_name,
        last_name: data.last_name,
        password: data.password,
        lab_name: data.lab_name,
        lab_branch_email: data.lab_branch_email,
        lab_mobile_number: data.lab_mobile_number,
        role: 'labUser',
        address: data.address,
      });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to create lab user');
    }
  }
);

export const deleteLabUser = createAsyncThunk(
  'users/deleteLabUser',
  async (id: number, thunkAPI) => {
    try {
      await apiClient.delete(`${config.services.ums}/api/v1/lab-users/${id}`);
      return id;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to delete lab user');
    }
  }
);

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearUsersError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLabUsers.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchLabUsers.fulfilled, (state, action) => { state.loading = false; state.labUsers = Array.isArray(action.payload?.data) ? action.payload.data : Array.isArray(action.payload) ? action.payload : []; })
      .addCase(fetchLabUsers.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      .addCase(fetchSuperAdmins.pending, (state) => { state.loading = true; })
      .addCase(fetchSuperAdmins.fulfilled, (state, action) => { state.loading = false; state.superAdmins = Array.isArray(action.payload?.data) ? action.payload.data : Array.isArray(action.payload) ? action.payload : []; })
      .addCase(fetchSuperAdmins.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      .addCase(createLabUser.fulfilled, (state) => { state.error = null; })
      .addCase(deleteLabUser.fulfilled, (state, action) => { state.labUsers = state.labUsers.filter(u => u.ID !== action.payload); });
  },
});

export const { clearUsersError } = usersSlice.actions;
export default usersSlice.reducer;
